import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { TrendingDown, TrendingUp, ArrowRight, ListChecks, Sparkles } from "lucide-react";
import CARSSkillCard from "@/components/CARSSkillCard";
import CoachToolModal from "@/components/CoachToolModal";
import {
  ResponsiveContainer,
  LineChart, Line,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  BarChart, Bar,
  Cell,
} from "recharts";

function chartColors(theme) {
  return {
    grid: theme === "dark" ? "#2f3428" : "#e5e2da",
    axis: theme === "dark" ? "#7a7e73" : "#8a918d",
    line: theme === "dark" ? "#7bb56e" : "#2d5a27",
    accent: theme === "dark" ? "#f0a583" : "#e88d67",
    tooltipBg: theme === "dark" ? "#1a1e19" : "#ffffff",
    tooltipBorder: theme === "dark" ? "#2f3428" : "#e5e2da",
    tooltipText: theme === "dark" ? "#ecebe4" : "#1c211f",
  };
}

function accuracyColor(acc, theme) {
  // gradient from weakness (red) → primary (green) based on accuracy
  if (acc >= 0.8) return theme === "dark" ? "#7bb56e" : "#4f7942";
  if (acc >= 0.6) return theme === "dark" ? "#a8c98a" : "#7ba55c";
  if (acc >= 0.4) return theme === "dark" ? "#e8c17b" : "#d9a552";
  return theme === "dark" ? "#ec8462" : "#d96c4a";
}

function shortDate(iso) {
  // "2026-02-05" → "Feb 5"
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Progress() {
  const [data, setData] = useState(null);
  const [aiTool, setAiTool] = useState(null); // { type, defaults }
  const { theme } = useTheme();
  const c = chartColors(theme);

  useEffect(() => {
    api.get("/progress").then((r) => setData(r.data));
  }, []);

  const startQuiz = (topic) => {
    setAiTool({
      type: "quiz",
      defaults: {
        subject: topic.subject_id || "biology",
        topic: topic.name || "",
        difficulty: "medium",
        count: 5,
      },
    });
  };

  if (!data) return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  const { mastered = [], weak = [], in_progress = [], stats = {}, trend = [], heatmap = [] } = data;

  const trendData = trend.map((t) => ({
    ...t,
    label: shortDate(t.date),
    accuracyPct: Math.round(t.accuracy * 100),
  }));

  const heatmapData = heatmap.map((h) => ({
    ...h,
    accuracyPct: Math.round(h.accuracy * 100),
  }));

  return (
    <div className="space-y-8" data-testid="progress-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-bold mb-3">Your progress</h1>
          <p className="text-lg" style={{ color: "var(--text-secondary)" }}>
            What you know cold, what needs work, and what&apos;s up next.
          </p>
        </div>
        <button
          onClick={() => setAiTool({ type: "quiz", defaults: { subject: "biology", difficulty: "medium", count: 5 } })}
          className="pm-btn-secondary inline-flex items-center gap-1.5 text-sm shrink-0"
          data-testid="progress-quiz-builder-btn"
          title="Generate an AI quiz on any subject or topic — Pro (0.25 credit)"
        >
          <Sparkles size={14} style={{ color: "var(--primary)" }} /> AI Quiz Builder
        </button>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="pm-card p-5" data-testid="progress-total-attempts">
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>Total attempts</div>
          <div className="font-display text-3xl font-bold mt-2">{stats.total_attempts}</div>
        </div>
        <div className="pm-card p-5" data-testid="progress-accuracy">
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>Accuracy</div>
          <div className="font-display text-3xl font-bold mt-2">{Math.round((stats.overall_accuracy || 0) * 100)}%</div>
        </div>
        <div className="pm-card p-5" data-testid="progress-topics-touched">
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>Topics touched</div>
          <div className="font-display text-3xl font-bold mt-2">{stats.topics_touched}</div>
        </div>
        <div className="pm-card p-5" data-testid="progress-mastered-count">
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>Mastered</div>
          <div className="font-display text-3xl font-bold mt-2" style={{ color: "var(--mastery)" }}>{mastered.length}</div>
        </div>
      </div>

      {/* CARS skill breakdown (renders nothing until user has ≥1 attempt) */}
      <CARSSkillCard />

      {/* Charts row */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Accuracy trend (line) */}
        <div className="pm-card p-6 lg:col-span-3" data-testid="progress-trend-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Accuracy over time</h2>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Last 30 days</span>
          </div>
          {trendData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>
              Answer a few questions to see your trend take shape.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData} margin={{ top: 8, right: 8, bottom: 4, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} />
                <XAxis dataKey="label" tick={{ fill: c.axis, fontSize: 11 }} stroke={c.axis} />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: c.axis, fontSize: 11 }}
                  stroke={c.axis}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: c.tooltipBg,
                    border: `1px solid ${c.tooltipBorder}`,
                    borderRadius: "0.75rem",
                    color: c.tooltipText,
                    fontSize: 12,
                  }}
                  formatter={(v, name, { payload }) => [
                    `${v}%  (${payload.correct}/${payload.total})`,
                    "Accuracy",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="accuracyPct"
                  stroke={c.line}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: c.line, stroke: c.line }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Subject heatmap (bar with per-cell color) */}
        <div className="pm-card p-6 lg:col-span-2" data-testid="progress-heatmap-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Subject heatmap</h2>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>Accuracy by subject</span>
          </div>
          {heatmapData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-center" style={{ color: "var(--text-muted)" }}>
              No subject data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={heatmapData} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 0 }}>
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fill: c.axis, fontSize: 11 }}
                  stroke={c.axis}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="subject_name"
                  tick={{ fill: c.axis, fontSize: 11 }}
                  stroke={c.axis}
                  width={110}
                />
                <Tooltip
                  contentStyle={{
                    background: c.tooltipBg,
                    border: `1px solid ${c.tooltipBorder}`,
                    borderRadius: "0.75rem",
                    color: c.tooltipText,
                    fontSize: 12,
                  }}
                  formatter={(v, _n, { payload }) => [
                    `${v}%  (${payload.correct}/${payload.total})`,
                    "Accuracy",
                  ]}
                />
                <Bar dataKey="accuracyPct" radius={[0, 6, 6, 0]}>
                  {heatmapData.map((entry, i) => (
                    <Cell key={i} fill={accuracyColor(entry.accuracy, theme)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Weak / Mastered lists */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
            <TrendingDown size={20} style={{ color: "var(--weakness)" }} /> Weakest topics
          </h2>
          {weak.length === 0 ? (
            <div className="pm-card p-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              No weak spots yet — keep going!
            </div>
          ) : (
            <div className="space-y-3" data-testid="weak-list">
              {weak.map((w, i) => <TopicRow key={i} data={w} tint="var(--weakness)" bg="var(--hl-yellow)" onQuiz={startQuiz} />)}
            </div>
          )}
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
            <TrendingUp size={20} style={{ color: "var(--mastery)" }} /> Mastered topics
          </h2>
          {mastered.length === 0 ? (
            <div className="pm-card p-6 text-sm" style={{ color: "var(--text-secondary)" }}>
              Answer 3+ questions with 80%+ accuracy to master a topic.
            </div>
          ) : (
            <div className="space-y-3" data-testid="mastered-list">
              {mastered.map((m, i) => <TopicRow key={i} data={m} tint="var(--mastery)" bg="var(--hl-green)" />)}
            </div>
          )}
        </div>
      </div>

      {in_progress.length > 0 && (
        <div>
          <h2 className="font-display text-2xl font-bold mb-4">In progress</h2>
          <div className="grid md:grid-cols-2 gap-3" data-testid="progress-in-progress-list">
            {in_progress.map((p, i) => <TopicRow key={i} data={p} tint="var(--primary)" bg="var(--muted)" onQuiz={startQuiz} />)}
          </div>
        </div>
      )}
      {aiTool && (
        <CoachToolModal
          open={!!aiTool}
          onClose={() => setAiTool(null)}
          type={aiTool.type}
          defaults={aiTool.defaults}
        />
      )}
    </div>
  );
}

function TopicRow({ data, tint, bg, onQuiz }) {
  const pct = Math.round(data.accuracy * 100);
  return (
    <div className="pm-card p-4 flex items-center justify-between gap-2">
      <Link to={`/app/topic/${data.topic.id}`} className="flex-1 min-w-0">
        <div className="font-display font-bold text-base truncate">{data.topic.name}</div>
        <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{data.topic.category}</div>
        <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tint }} />
        </div>
      </Link>
      <div className="flex items-center gap-2 shrink-0">
        <div className="pm-badge" style={{ background: bg, color: tint }}>{pct}%</div>
        {onQuiz && (
          <button
            onClick={(e) => { e.preventDefault(); onQuiz(data.topic); }}
            className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full transition-colors"
            style={{ background: "var(--paper)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
            data-testid={`quiz-me-${data.topic.id}`}
            title="Generate an AI quiz on this topic — Pro (0.25 credit)"
          >
            <ListChecks size={11} /> Quiz me
          </button>
        )}
        <ArrowRight size={16} style={{ color: "var(--text-muted)" }} />
      </div>
    </div>
  );
}
